/* SliderPreviewComponent */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, h, Host, Event, EventEmitter, State, Listen } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import { AbstractComponent } from '@cewe-designsystem/abstract';
import { PreviewSelect, PreviewRegister } from './types/events';

/**
 *
 */
@Component({
  tag: 'cwc-slider-preview',
  styleUrl: './styles/cwc-slider-preview.scss',
  shadow: true,
})
export class SliderPreviewComponent {
  /**
   * element
   */
  @Element() element: HTMLElement;

  @State() dragging: boolean = false;
  @State() sliderIsMoving: boolean = false;

  private canvas: HTMLElement;

  /**
   * Register Preview in Slider to pass functions
   *
   * @docs
   * @event on:slider-preview:register
   */
  @Event({
    eventName: 'on:slider-preview:register',
  })
  previewRegister: EventEmitter<PreviewRegister>;

  /**
   * Event thrown when preview element was clicked
   *
   * @docs
   * @event on:slider-preview:select
   */
  @Event({
    eventName: 'on:slider-preview:select',
  })
  previewSelect: EventEmitter<PreviewSelect>;

  /**
   * Called every time the component is connected to the DOM.
   * When the component is first connected, this method is called before componentWillLoad.
   * It is important to note that this method can be called more than once, everytime, the element is attached or moved in the DOM.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec
   */
  connectedCallback() {}

  /**
   * Called every time the component is disconnected from the DOM, i.e., it calls be dispatched more than once
   * DO not confuse with a "onDestroy" kind of event.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec.
   */
  disconnectedCallback() {}

  /**
   * Called once just after the component is first connected to the DOM.
   * A promise can be returned, that can be used to wait for the first render.
   */
  componentWillLoad() {}

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   */
  componentDidLoad() {
    this.initPreview();

    this.element.shadowRoot.querySelector('slot').addEventListener('slotchange', () => {
      console.debug('SliderPreview slot change detected');
      this.initPreview();
    });

    this.handleMouseDragging();
  }

  /**
   * Called before every render().
   * A promise can be returned, that can be used to wait for the upcoming render.
   */
  componentWillRender() {}

  /**
   * Called after every render().
   */
  componentDidRender() {}

  /**
   * Called when the component is about to be updated because some Prop() or State() changed.
   * It is never called during the first render().
   * A promise can be returned, that can be used to wait for the next render.
   */
  componentWillUpdate() {}

  /**
   * Called just after the component updates. It is never called during the first render().
   */
  componentDidUpdate() {}

  @Listen('click')
  handleClicks(e: Event) {
    const targetPath = (e.composedPath() as HTMLElement[]).filter(
      (el) => el instanceof HTMLElement && el.hasAttribute('was-processed'),
    );

    if (targetPath.length === 0) {
      return;
    }

    const index: number = Number.parseInt(targetPath[0].getAttribute('data-origin')) ?? 0;
    if (this.dragging) {
      console.debug('click blocked -> dragging');
      return;
    }
    this.previewSelect.emit({ selectedPreviewIndex: index });
    (targetPath[0] as HTMLElement).focus();
  }

  @Listen('keydown')
  handleKeyboard(event: KeyboardEvent) {
    const targetPath = (event.composedPath() as HTMLElement[]).filter(
      (el) => el instanceof HTMLElement && el.hasAttribute('was-processed'),
    );

    if (targetPath.length === 0) {
      return;
    }

    const index: number = Number.parseInt(targetPath[0].getAttribute('data-origin')) ?? 0;
    const slidesCount: number = this.element.querySelectorAll('[was-processed]').length;

    if (this.sliderIsMoving) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }

    let newIndex = null;
    switch (event.key) {
      case 'ArrowLeft':
        newIndex = index == 0 ? slidesCount - 1 : index - 1;
        break;
      case 'ArrowRight':
        newIndex = index == slidesCount - 1 ? 0 : index + 1;
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = slidesCount - 1;
        break;
      default:
        break;
    }

    if (newIndex !== null) {
      event.stopPropagation();
      event.preventDefault();

      const oldIndex = this.element.querySelectorAll('[data-active]').item(0).getAttribute('data-origin');
      if (newIndex != oldIndex) {
        this.previewSelect.emit({ selectedPreviewIndex: newIndex });
        this.element.querySelector<HTMLElement>(`[data-origin="${newIndex}"]`).focus();
        this.sliderIsMoving = true;
      }
    }
  }

  private initPreview() {
    console.log('initPreview');
    //set attributes and event listener to slot elements
    const previewElements = this.element.children;
    Array.from(previewElements)
      .filter((previewElement) => !previewElement.hasAttribute('was-processed'))
      .forEach((previewElement, index) => {
        previewElement.setAttribute('data-origin', `${index}`);
        previewElement.setAttribute('tabindex', '-1');
        previewElement.setAttribute('was-processed', 'true');
      });

    //register preview in slider
    this.previewRegister.emit({ setActivePreview: this.setActivePreview.bind(this) });
  }

  private handleMouseDragging(): void {
    let startX: number = 0;
    let scrollLeft: number = 0;

    const handleMouseMove = (event: MouseEvent) => {
      this.dragging = true;
      this.canvas.scrollLeft = scrollLeft - (event.clientX - startX);
    };

    this.canvas.addEventListener('mousedown', (event: MouseEvent) => {
      event.preventDefault();

      startX = event.clientX;
      scrollLeft = this.canvas.scrollLeft;

      document.addEventListener(
        'mouseup',
        () => {
          setTimeout(() => (this.dragging = false), 50); /* eslint-disable-line no-magic-numbers */
          document.removeEventListener('mousemove', handleMouseMove);
        },
        { once: true, passive: true },
      );

      document.addEventListener('mousemove', handleMouseMove, { passive: true });
    });
  }

  private setActivePreview(visibleSlides: Array<number>) {
    const stack = {};
    Error.captureStackTrace(stack);
    console.debug('set active', visibleSlides, stack);

    //remove active state from every element that was active
    Array.from(this.element.querySelectorAll('[data-active]')).forEach((preview) => {
      preview.removeAttribute('data-active');
      preview.setAttribute('tabindex', '-1');
    });

    //mark visible preview elements as active
    let first = false;
    visibleSlides.forEach((index) => {
      const previewElement = this.element.querySelector(`[data-origin="${index}"]`) as HTMLElement;
      previewElement.setAttribute('data-active', '');

      if (!first) {
        previewElement.setAttribute('tabindex', '0');
        first = true;
      }
    });

    //focus the preview element in the center of the active previews
    const activeSlideCenterIndex = visibleSlides[Math.floor(visibleSlides.length / 2)];
    const centerPreview = this.element.querySelector<HTMLElement>(`[data-origin="${activeSlideCenterIndex}"]`);
    this.canvas.scrollTo({
      left: centerPreview.offsetLeft - (this.canvas.clientWidth / 2 - centerPreview.clientWidth / 2),
      behavior: 'smooth',
    });

    this.sliderIsMoving = false;
  }

  /**
   * render
   */
  render() {
    return (
      <Host>
        <div ref={(el) => (this.canvas = el as HTMLElement)} class="canvas" data-dragging={this.dragging} tabindex="-1">
          <slot />
        </div>
      </Host>
    );
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(SliderPreviewComponent.prototype, AbstractComponent.prototype);

export interface SliderPreviewComponent extends AbstractComponent {}
